import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UserService } from "../../services/user/user.service";
import { ProductLinkService } from "../../services/product-link/product-link.service";
import { MerchantService } from "../../services/merchant/merchant.service";
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NeedAuthorize } from "../../classes/needAuthorize";
import { Global } from "../../classes/global";
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from "ngx-toastr";

@Component({
  selector: 'app-product-link-list',
  templateUrl: './product-link-list.component.html',
  styleUrls: ['./product-link-list.component.scss']
})
export class ProductLinkListComponent extends NeedAuthorize {
  @ViewChild('productLinkModal') productLinkModal: ElementRef;
  @ViewChild('bulkAddModal') bulkAddModal: ElementRef;

  query = {
    queryString: '',
    pageSize: 100,
    pageNumber: 1,
    merchantGroupId: '',
    outOfStockOnly: false,
    priceChangedOnly: false
  };

  selectedIds = new Set<string>();

  queryResult: any = {
    items: [],
    total: 0
  };

  productLinkDetail: any = {
    id: '',
    url: '',
    productName: '',
    imageUrl: '',
    isInStock: false,
    price: '',
    previousPrice: '',
    shipFrom: '',
    soldBy: '',
    shipTime: '',
    lastCrawledTime: null,
    merchantGroupId: '',
    merchantGroup: {
      id: '',
      name: ''
    }
  };

  merchantGroups = [];
  
  bulkAddData: any = {
    merchantGroupId: '',
    urls: ''
  };

  userData: any;

  constructor(protected router: Router,
    protected userService: UserService,
    protected titleService: Title,
    private global: Global,
    private modalService: NgbModal,
    private productLinkService: ProductLinkService,
    private merchantService: MerchantService,
    private toastr: ToastrService) {
    super(router, userService, titleService);
    this.queryProductLinks();
    this.getMerchantGroups();
  }

  ngOnInit(): void {
  }

  queryProductLinks(page = undefined) {
    if (page != undefined) {
      this.query.pageNumber = page;
    }
    this.global.pageLoading = true;
    this.productLinkService.queryProductLinks(this.query).subscribe(data => {
      this.queryResult = data;
      this.clearSelection();
      this.global.pageLoading = false;
    }, error => {
      this.global.pageLoading = false;
      this.toastr.error('Error loading product links');
    });
  }

  onSearchChange() {
    this.query.pageNumber = 1;
    this.clearSelection();
    this.queryProductLinks();
  }

  onMerchantGroupFilterChange() {
    this.query.pageNumber = 1;
    this.clearSelection();
    this.queryProductLinks();
  }

  onOutOfStockFilterChange() {
    this.query.pageNumber = 1;
    this.clearSelection();
    this.queryProductLinks();
  }

  onPriceChangedFilterChange() {
    this.query.pageNumber = 1;
    this.clearSelection();
    this.queryProductLinks();
  }

  selectAllPriceChanged() {
    const oneOffQuery = { ...this.query, priceChangedOnly: true, pageNumber: 1, pageSize: 500 };
    this.global.pageLoading = true;
    this.productLinkService.queryProductLinks(oneOffQuery).subscribe(data => {
      (data.items || []).forEach((pl: any) => this.selectedIds.add(pl.id));
      this.selectedIds = new Set(this.selectedIds);
      this.global.pageLoading = false;
      this.toastr.info(`Selected ${this.selectedIds.size} price-changed item(s).`);
    }, () => {
      this.global.pageLoading = false;
    });
  }

  clearSelection() {
    this.selectedIds.clear();
  }

  toggleSelect(pl: any) {
    const id = pl.id;
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
    this.selectedIds = new Set(this.selectedIds);
  }

  isSelected(pl: any): boolean {
    return pl && this.selectedIds.has(pl.id);
  }

  toggleSelectAll() {
    if (this.isAllSelected()) {
      this.selectedIds.clear();
    } else {
      this.queryResult.items.forEach((pl: any) => this.selectedIds.add(pl.id));
    }
    this.selectedIds = new Set(this.selectedIds);
  }

  isAllSelected(): boolean {
    const items = this.queryResult.items || [];
    return items.length > 0 && items.every((pl: any) => this.selectedIds.has(pl.id));
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  deleteSelected() {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0) {
      this.toastr.warning('No rows selected');
      return;
    }
    if (!confirm(`Delete ${ids.length} selected product link(s)?`)) {
      return;
    }
    this.global.pageLoading = true;
    let completed = 0;
    let errorCount = 0;
    ids.forEach(id => {
      this.productLinkService.deleteProductLink(id).subscribe(
        () => {
          completed++;
          this.selectedIds.delete(id);
          if (completed === ids.length) {
            this.selectedIds = new Set(this.selectedIds);
            this.global.pageLoading = false;
            this.toastr.success(`Deleted ${completed - errorCount} product link(s)`);
            this.queryProductLinks();
          }
        },
        () => {
          errorCount++;
          completed++;
          if (completed === ids.length) {
            this.selectedIds = new Set(this.selectedIds);
            this.global.pageLoading = false;
            if (errorCount > 0) {
              this.toastr.warning(`Deleted ${completed - errorCount} product link(s), ${errorCount} failed`);
            } else {
              this.toastr.success(`Deleted ${completed} product link(s)`);
            }
            this.queryProductLinks();
          }
        }
      );
    });
  }

  openProductLinkModal(productLinkId) {
    if (productLinkId) {
      this.global.pageLoading = true;
      this.productLinkService.getProductLink(productLinkId)
        .subscribe(data => {
          this.global.pageLoading = false;
          this.productLinkDetail = data;
          this.modalService.open(this.productLinkModal, { windowClass: "lModal", size: 'lg' });
        }, error => {
          this.global.pageLoading = false;
          this.toastr.error('Error loading product link');
        });
    }
    else {
      // For new product link, auto-set the first granted merchant group
      const firstGroup = this.merchantGroups && this.merchantGroups.length > 0 ? this.merchantGroups[0] : null;
      this.productLinkDetail = {
        id: '',
        url: '',
        productName: '',
        imageUrl: '',
        isInStock: false,
        price: '',
        previousPrice: '',
        shipFrom: '',
        soldBy: '',
        shipTime: '',
        lastCrawledTime: null,
        merchantGroupId: firstGroup ? firstGroup.id : '',
        merchantGroup: firstGroup || {
          id: '',
          name: ''
        }
      };
      this.modalService.open(this.productLinkModal, { windowClass: "lModal", size: 'lg' });
    }
  }

  getMerchantGroups() {
    this.merchantService.getGrantedMerchantGroups().subscribe(data => {
      this.merchantGroups = data;
    });
  }

  saveProductLink() {
    if (!this.productLinkDetail.url) {
      this.toastr.error('URL is required');
      return;
    }
    
    // Ensure merchant group is set (use first granted group if not set)
    if (!this.productLinkDetail.merchantGroupId && this.merchantGroups && this.merchantGroups.length > 0) {
      this.productLinkDetail.merchantGroupId = this.merchantGroups[0].id;
    }
    
    if (!this.productLinkDetail.merchantGroupId) {
      this.toastr.error('No merchant group available');
      return;
    }

    this.global.pageLoading = true;
    this.productLinkService.saveProductLink(this.productLinkDetail).subscribe(data => {
      this.global.pageLoading = false;
      this.modalService.dismissAll();
      this.toastr.success('Product link saved successfully');
      this.queryProductLinks();
    }, error => {
      this.global.pageLoading = false;
      this.toastr.error('Error saving product link: ' + (error.error?.message || error.message || 'Unknown error'));
    });
  }

  deleteProductLink(productLink) {
    if (confirm('Are you sure you want to delete this product link?')) {
      this.global.pageLoading = true;
      this.productLinkService.deleteProductLink(productLink.id).subscribe(data => {
        this.global.pageLoading = false;
        this.toastr.success('Product link deleted successfully');
        this.queryProductLinks();
      }, error => {
        this.global.pageLoading = false;
        this.toastr.error('Error deleting product link');
      });
    }
  }

  formatDate(date: string) {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return date;
    }
  }

  /**
   * Parse price string (e.g. "$12.99" or "12,99") to number for diff.
   */
  private parsePrice(priceStr: string): number | null {
    if (priceStr == null || typeof priceStr !== 'string') return null;
    const cleaned = priceStr.replace(/[$,]/g, '').trim();
    if (!cleaned) return null;
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
  }

  /**
   * Return price diff info for template: show up/down arrow and green/red diff when we have previous price.
   * - Price went up: red, up arrow.
   * - Price went down: green, down arrow.
   */
  getPriceDiff(pl: { previousPrice?: string; price?: string }): { hasDiff: boolean; isUp: boolean; isDown: boolean; diffText: string } {
    const prev = this.parsePrice(pl?.previousPrice);
    const curr = this.parsePrice(pl?.price);
    const out = { hasDiff: false, isUp: false, isDown: false, diffText: '' };
    if (prev == null || curr == null) return out;
    const diff = curr - prev;
    if (diff === 0) return out;
    out.hasDiff = true;
    out.isUp = diff > 0;
    out.isDown = diff < 0;
    const sign = diff > 0 ? '+' : '';
    out.diffText = `${sign}$${diff.toFixed(2)}`;
    return out;
  }

  openBulkAddModal() {
    // Auto-set the first granted merchant group
    const firstGroup = this.merchantGroups && this.merchantGroups.length > 0 ? this.merchantGroups[0] : null;
    this.bulkAddData = {
      merchantGroupId: firstGroup ? firstGroup.id : '',
      urls: ''
    };
    this.modalService.open(this.bulkAddModal, { windowClass: "lModal", size: 'lg' });
  }

  saveBulkProductLinks() {
    if (!this.bulkAddData.merchantGroupId) {
      this.toastr.error('Merchant Group is required');
      return;
    }
    
    if (!this.bulkAddData.urls || !this.bulkAddData.urls.trim()) {
      this.toastr.error('At least one product link URL is required');
      return;
    }

    // Split URLs by newline and filter out empty lines
    const urls = this.bulkAddData.urls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0);

    if (urls.length === 0) {
      this.toastr.error('No valid URLs found. Please enter at least one URL.');
      return;
    }

    // Dedupe by product ID per seller: same merchant group + same SKU/product ID => keep one
    const seen = new Map<string, string>();
    const dedupedUrls: string[] = [];
    for (const url of urls) {
      const key = this.getProductIdFromUrl(url) || url;
      if (!seen.has(key)) {
        seen.set(key, url);
        dedupedUrls.push(url);
      }
    }
    if (dedupedUrls.length < urls.length) {
      this.toastr.info(`Removed ${urls.length - dedupedUrls.length} duplicate SKU(s) within this list.`);
    }

    this.global.pageLoading = true;
    
    // Create product links for each URL
    const productLinks = dedupedUrls.map(url => ({
      url: url,
      merchantGroupId: this.bulkAddData.merchantGroupId,
      productName: '',
      imageUrl: '',
      isInStock: false,
      price: '',
      shipFrom: '',
      soldBy: '',
      shipTime: ''
    }));

    // Save all product links
    let successCount = 0;
    let errorCount = 0;
    let completed = 0;

    productLinks.forEach((link) => {
      this.productLinkService.saveProductLink(link).subscribe(
        data => {
          successCount++;
          completed++;
          if (completed === productLinks.length) {
            this.global.pageLoading = false;
            this.modalService.dismissAll();
            if (errorCount === 0) {
              this.toastr.success(`Successfully added ${successCount} product link(s)`);
            } else {
              this.toastr.warning(`Added ${successCount} product link(s), ${errorCount} failed`);
            }
            this.queryProductLinks();
          }
        },
        error => {
          errorCount++;
          completed++;
          if (completed === productLinks.length) {
            this.global.pageLoading = false;
            this.modalService.dismissAll();
            if (successCount > 0) {
              this.toastr.warning(`Added ${successCount} product link(s), ${errorCount} failed`);
            } else {
              this.toastr.error('Error adding product links: ' + (error.error?.message || error.message || 'Unknown error'));
            }
            this.queryProductLinks();
          }
        }
      );
    });
  }

  getLinkText(url: string): string {
    if (!url) return '-';
    try {
      const dpIndex = url.indexOf('/dp/');
      if (dpIndex !== -1) {
        const afterDp = url.substring(dpIndex + 4);
        const nextSlash = afterDp.indexOf('/');
        if (nextSlash !== -1) {
          return afterDp.substring(0, nextSlash);
        }
        const nextQuestion = afterDp.indexOf('?');
        if (nextQuestion !== -1) {
          return afterDp.substring(0, nextQuestion);
        }
        return afterDp;
      }
      const gpIndex = url.indexOf('/gp/product/');
      if (gpIndex !== -1) {
        const afterGp = url.substring(gpIndex + 12);
        const end = Math.min(
          afterGp.indexOf('/') >= 0 ? afterGp.indexOf('/') : afterGp.length,
          afterGp.indexOf('?') >= 0 ? afterGp.indexOf('?') : afterGp.length
        );
        return afterGp.substring(0, end);
      }
      return url;
    } catch {
      return url;
    }
  }

  /** Product ID from URL for deduplication (same as getLinkText for /dp/). */
  getProductIdFromUrl(url: string): string {
    const t = this.getLinkText(url);
    return t === '-' ? (url || '') : t;
  }
}
